import React from 'react';
import Autocomplete from 'components/FormElements/AppAutocomplete';
import { CircularProgress, TextField } from '@mui/material';
import FieldRow from 'components/FormElements/FieldRow';
import useGetInvestorsLeanResponse from 'hooks/data/investor/useGetInvestorsLeanResponse';

type Investor = {
  _id: string;
  email: string;
  fullName: string;
};

type Props = {
  onInvestorSelect: (investorId: string | null) => void;
  selectedInvestorId: string | null;
  queuedWithdrawals: Array<{ _id: string }>;
  firmId: string;
  disabled?: boolean;
};

const InvestorSearch: React.FC<Props> = ({
  onInvestorSelect,
  selectedInvestorId,
  queuedWithdrawals,
  firmId,
  disabled = false,
}) => {
  const investors = useGetInvestorsLeanResponse({ firmId });

  const selectedInvestor = investors.data?.find(
    (inv) => inv._id === selectedInvestorId,
  );

  const investorNotAlreadyQueued = (investor: Investor) =>
    !queuedWithdrawals.some((withdrawal) => withdrawal._id === investor._id);

  return (
    <FieldRow>
      <Autocomplete
        options={investors.data?.filter(investorNotAlreadyQueued) ?? []}
        id="investorSearch"
        getOptionLabel={(option) => `${option.fullName} (${option.email})`}
        getOptionKey={(option) => option._id}
        filterSelectedOptions
        loading={investors.isFetching}
        disabled={disabled}
        onChange={(_, investor) => {
          if (!investor) return onInvestorSelect(null);
          onInvestorSelect(investor._id);
        }}
        value={selectedInvestor || null}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder="Search for an investor"
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {investors.isFetching || disabled ? (
                      <CircularProgress size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </FieldRow>
  );
};

export default InvestorSearch;
